/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-07 14:35:53                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:19:28                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * MachineSelect: Machine selection field
    * getMachineList: get machine list depend on customerId
    * handleMachineCreate: handle machine creation event
*/

/* Imports */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../../services/api/error";
import { t } from "i18next";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { validator } from "../../../services/form-validator/form-validator";
/***/

/* Components */
import EditCreateDrawer from "../../edit-create-drawer/EditCreateDrawer";
import MachineForm, { MachineFormValidator } from "./MachineForm";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

/**
* MachineSelect: Machine selection field
* allow create/edit
* @param value - previous selected value
* @param customerId - related customer's id
* @param onChange - on value change event
* @param className - additional classname 
*/
export default function MachineSelect({
  value,
  customerId,
  onChange,
  className = ""
}: {
  value?: EasyTech.Machine[],
  customerId: string,
  onChange: (e: EasyTech.Machine[]) => void,
  className?: string
}) {
  const [valueState, setValueState] = useState<any[]>([]);
  const [timestamp, setTimestamp] = useState<string>(String(Date.now()));

  /**
  * useEffect: keep value state update
  * reload on value change 
  */
  useEffect(() => {
    if (value) {
      setValueState(value.map((el) => ({
        label: `${el.brand.label}${el.type ? ` - ${el.type.label}` : ""}${el.serialNumber ? ` - ${el.serialNumber}` : ""}`,
        value: el
      })));
    } else setValueState([]);
  }, [value]);
  /***/

  /**
  * getMachineList: get machine list depend on customerId 
  * @param inputValue - search input value for filtering machines
  * @return - list of machine displayable in select
  */
  const getMachineList = (inputValue = ""): Promise<EasyTech.IFormData<EasyTech.Machine>[]> => {
    return new Promise(resolve => {
      api.get(`users/customers/${customerId}/machines`).then((ret) => {
        const tmp = ret as EasyTech.Machine[];

        // Filter machines based on search input
        const filteredMachines = tmp.filter(machine => {
          if (!inputValue) return true; // Show all if no search input
          
          const searchTerm = inputValue.toLowerCase();
          const brandLabel = machine.brand?.label?.toLowerCase() || "";
          const typeLabel = machine.type?.label?.toLowerCase() || "";
          const serialNumber = machine.serialNumber?.toLowerCase() || "";
          const fullLabel = `${brandLabel} ${typeLabel} ${serialNumber}`.toLowerCase();
          
          return fullLabel.includes(searchTerm) || 
                 brandLabel.includes(searchTerm) ||
                 typeLabel.includes(searchTerm) ||
                 serialNumber.includes(searchTerm);
        });

        return resolve(setSelectList(
          filteredMachines.filter(el => el.customer ? el.customer.id === customerId : true), 
          (el) => `${el.brand.label}${el.type ? ` - ${el.type.label}` : ""}${el.serialNumber ? ` - ${el.serialNumber}` : ""}`
        ));
      }).catch((err) => {
        console.error(err);
        return resolve([]);
      });
    });
  };
  /***/

  /**
  * handleMachineCreate: handle machine creation event
  * update select list 
  * @param machine - machine's data to create
  */
  const handleMachineCreate = (machine: EasyTech.Machine): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(machine, MachineFormValidator)) {
        api.post("users/machines", {
          ...machine,
          customer: {id: customerId}
        })
          .then((ret) => {
            const ma = ret as EasyTech.Machine;
            const tmp: EasyTech.Machine[] = value !== undefined ? [...value, ma] : [ma];
            onChange(tmp);
            setTimestamp(String(Date.now()));
            return resolve();
          }).catch((err) => {
            console.error(err);
            showApiErrorToast(err, { fallbackMessage: "Erreur lors de la création de la machine" });
            return reject();
          });
      } else {
        const fieldNames = {
          serialNumber: t("common:serialNumber"),
          releaseYear: t("common:releaseYear"),
          type: t("common:type"),
          brand: t("common:brand")
        };
        
        const errorMessage = validator.getFormErrorMessage(machine, MachineFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  /***/

  return (
    <div className="flex">
      <MultiCheckSelect  key={`${customerId}-${timestamp}`}
                    cacheOptions={false}
                    defaultOptions={true}
                    loadOptions={getMachineList}
                    value={valueState}
                    theme={reactSelectTheme}
                    onChange={(e: { value: EasyTech.Machine }[] | null) => onChange((e ?? []).map((el) => el.value))}
                    className={`w-full mr-2 ${className}`}
                    name="machine"
                    placeholder="Search or select machines"
                    classNamePrefix="react-select"
                    noOptionsMessage={({ inputValue }: { inputValue: string }) => 
                      inputValue ? `No machines found for "${inputValue}"` : "No machines available"
                    }
                    loadingMessage={() => "Searching machines..."}
                    filterOption={null} // Disable client-side filtering since we filter server-side
                    />

      <EditCreateDrawer<EasyTech.Machine>   FormComponent={MachineForm}
                                            isNotEditable={true}
                                            isNotCreatable={false}
                                            entityName="machine"
                                            onSubmit={handleMachineCreate}/>
    </div>
  );
}
/***/
